// ============================================================
// PERRY — PYRE & ASH THEME v1  (July 26, 2026)
// Charred iron chrome, ember copper metal, bone-ash text,
// spectral teal ghostlight, blood rose vitals — a burned world
// still warm to the touch.
//
// HARVEST NOTE (pattern from Terror theme.js v4):
//   • The STRUCTURE is Terror's — two-namespace palette law,
//     material layer, facets, shadow stacks, glow helpers.
//   • Every VALUE is Perry's.
//   • EXPORT NAMES ARE PRESERVED BYTE-FOR-BYTE (the compat law):
//     GM/SD keys, GOLD_METAL, OBSIDIAN_*, MANUSCRIPT_BG, facet(),
//     bloom(), textGlow(), etc. — so any harvested Terror UI file
//     (AchievementToast, KeyboardNav, future StartMenu) recolors
//     to Perry without touching a single call site.
//     "GOLD_METAL" here is ember copper. "OBSIDIAN_*" is char.
//     The names are the contract; the metal changed hands.
//
// THE PALETTE LAW (unchanged, non-negotiable):
//   SD = the WORLD — the 3D stage, fire, night, the road.
//   GM = ALL UI CHROME — menus, HUD, codex, overlays.
//   Never cross the streams.
//
// THE MATERIAL LAWS (inherited from Terror v4):
//   1. No flat fills — every surface is a gradient with a light
//      source (ember above, char below).
//   2. Borders are METAL — gradient strips, never 1px hairlines.
//   3. Faceted geometry on interactive elements (facet()).
//      ⚠ clip-path clips box-shadows — use filter: drop-shadow.
//   4. Everything pressable has depth.
//   5. Active = LIGHT, not color-swap — selected things emit.
//   6. Ambient life via ensureKeyframes only — never <style> in JSX.
//   7. Text has presence — textGlow on embers, TEXT_SET on body.
// ============================================================

// ── World colors (the 3D stage / fire / night) — never in chrome ──
export const SD = {
  void0: "#050302",
  void1: "#0a0605",
  void2: "#120a08",
  void3: "#1d100b",
  void4: "#2c1810",

  starWhite: "#efe4d2",
  starSilver: "#cbbba6",
  starDim: "#8f7d6a",

  gold: "#d98e3a",          // ember gold — firelight on the road
  goldBright: "#ffb14e",
  goldDim: "#5c3a14",

  purple: "#5b2a6e",        // the bruised night above the smoke
  purpleLight: "#a06ac0",
  purpleDim: "#241030",

  teal: "#1a8a80",          // Perry's ghostlight — the spirit ember
  tealLight: "#3fe0cf",
  tealDim: "#0a2f2b",

  red: "#d43a24",           // open flame
  redDim: "#6e1a10",
  redMist: "#8e2416",

  blue: "#3b6ea8",
  blueDim: "#17293f",

  fontDisplay: "'Cinzel', 'Palatino Linotype', serif",
  fontMono: "'Courier New', Courier, monospace",
};

// ── Pyre & Ash palette — use for ALL UI chrome ───────────────
// Charred iron with ember veins, copper metal, bone-ash text,
// spectral teal accent, blood rose vitals.
export const GM = {
  // Char steps — page → raised surface (deepest to lightest)
  page:          "#0a0605",       // the cold-hearth floor
  pageWarm:      "#120a08",       // shallow char (legacy name — still warm here)
  pageMid:       "#1c1009",       // raised slab
  pageLight:     "#28170c",       // lit slab / hover surface
  pageBorder:    "#4a2c1a",       // scorched-iron edge line

  // Ember copper — Perry's primary metal
  inkGold:       "#c8763a",       // forged ember copper — primary accent
  inkGoldBright: "#f0984a",       // copper catching the fire
  inkGoldDim:    "#7a4218",       // copper in shadow
  inkBrown:      "#6e5240",       // ash-iron — muted borders (legacy name)
  inkRed:        "#a02616",       // blood rubric

  // Bone-ash text
  vellum:        "#e9ddc8",       // main text
  vellumDim:     "#b3a288",       // secondary
  vellumFaint:   "#7d6f5e",       // muted / disabled

  // Ghost silver — cooled smoke ornament
  silver:        "#c0c4c8",
  silverBright:  "#e8ecef",
  silverDim:     "#5e6469",

  // Spectral teal — Perry's ghostlight accent (legacy "amethyst" keys)
  amethyst:       "#2bb8a8",
  amethystBright: "#4ff0dc",
  amethystDim:    "#123b36",

  // Blood rose — hearts / Anima / vitals
  rose:          "#f4726a",
  roseDim:       "#8e3a30",

  // Decorative
  ruling:        "rgba(140,110,80,0.16)",
  candlelight:   "radial-gradient(ellipse at 50% 0%, rgba(240,152,74,0.10) 0%, rgba(43,184,168,0.05) 40%, transparent 70%)",

  font:     "'Cinzel', 'Palatino Linotype', serif",
  fontD:    "'Cinzel Decorative', 'Palatino Linotype', serif",
  fontBody: "'Cormorant Garamond', 'Palatino Linotype', serif",
  fontUI:   "'Inter', 'Segoe UI', system-ui, sans-serif",
};

// ── Semantic type colors (Perry vocabulary) ──────────────────
export const TYPE_COLORS_SD = {
  "Tome":     "#3fe0cf",
  "Impera":   "#fbbf24",
  "U'hlti":   "#a06ac0",
  "Curio":    "#fb923c",
  "Prophecy": "#818cf8",
};

export const ILK_COLORS_SD = {
  "Space":   "#4aa8e0",
  "Phantom": "#a06ac0",
  "Dragon":  "#f87171",
  "Time":    "#fbbf24",
};

// ── World background — for DOM screens that want the night road ──
export const STARFIELD_BG = `
  radial-gradient(ellipse at 30% 90%, rgba(212,58,36,0.30) 0%, rgba(212,58,36,0.06) 40%, transparent 65%),
  radial-gradient(ellipse at 70% 85%, rgba(217,142,58,0.22) 0%, transparent 55%),
  radial-gradient(ellipse at 50% 10%, rgba(91,42,110,0.25) 0%, transparent 60%),
  radial-gradient(ellipse at 15% 30%, rgba(26,138,128,0.10) 0%, transparent 40%),
  #050302
`;

// ── Char page background — for all UI screens ────────────────
// (Keeps the legacy names so harvested screens recolor free.)
export const OBSIDIAN_BG = [
  "repeating-linear-gradient(135deg, transparent 0px, transparent 26px, rgba(240,152,74,0.026) 26px, rgba(240,152,74,0.026) 27px)",
  "radial-gradient(ellipse at 28% 0%, rgba(200,118,58,0.10) 0%, transparent 52%)",
  "radial-gradient(ellipse at 78% 92%, rgba(74,44,26,0.34) 0%, transparent 55%)",
  "radial-gradient(ellipse at 8% 80%, rgba(160,38,22,0.08) 0%, transparent 45%)",
  "#0a0605",
].join(", ");
export const MANUSCRIPT_BG = OBSIDIAN_BG;

// ═════════════════════════════════════════════════════════════
// THE MATERIAL LAYER — gradients, shadows, facets
// Same recipes, Perry's metals. GOLD_METAL is ember copper;
// OBSIDIAN_* is char. Names preserved by the compat law.
// ═════════════════════════════════════════════════════════════

export const GOLD_METAL =
  "linear-gradient(160deg, #ffbf70 0%, #8e4a16 22%, #f0984a 40%, #5a2c0c 62%, #ffab58 82%, #7a4218 100%)";
export const GOLD_METAL_H =
  "linear-gradient(90deg, transparent, #8e4a16 12%, #f0984a 35%, #ffbf70 50%, #f0984a 65%, #8e4a16 88%, transparent)";
export const SILVER_METAL =
  "linear-gradient(160deg, #f2f5f7 0%, #6e767c 22%, #ced4d8 42%, #4c5358 64%, #e2e8ec 84%, #5e6469 100%)";
export const SILVER_METAL_H =
  "linear-gradient(90deg, transparent, #6e767c 12%, #ced4d8 35%, #f2f5f7 50%, #ced4d8 65%, #6e767c 88%, transparent)";

// Char surfaces — never flat. Ember source top-left.
export const OBSIDIAN_SLAB =
  "linear-gradient(175deg, #28170c 0%, #1c1009 55%, #100906 100%)";
export const OBSIDIAN_SLAB_LIT =
  "linear-gradient(175deg, #4a2810 0%, #301a0b 50%, #1c1007 100%)";
export const OBSIDIAN_RECESS =
  "linear-gradient(175deg, #060302 0%, #120a08 60%, #180e08 100%)";
export const OBSIDIAN_PANEL =
  "radial-gradient(ellipse at 30% 0%, #34200f 0%, #180d07 45%, #0a0605 100%)";

// Faceted geometry — cut-corner clip-paths.
// ⚠ clip-path clips box-shadow: pair with filter: drop-shadow.
export const facet = (n = 12) =>
  `polygon(${n}px 0, calc(100% - ${n}px) 0, 100% ${n}px, 100% calc(100% - ${n}px), calc(100% - ${n}px) 100%, ${n}px 100%, 0 calc(100% - ${n}px), 0 ${n}px)`;
export const facetTB = (n = 10) =>
  `polygon(${n}px 0, 100% 0, 100% calc(100% - ${n}px), calc(100% - ${n}px) 100%, 0 100%, 0 ${n}px)`;

// Shadow stacks
export const SHADOW_RAISED =
  "inset 0 1px 0 rgba(232,236,239,0.14), 0 3px 10px rgba(0,0,0,0.6)";
export const SHADOW_FLOAT =
  "inset 0 1px 0 rgba(255,191,112,0.28), 0 10px 34px rgba(0,0,0,0.78), 0 0 44px rgba(200,118,58,0.10)";
export const SHADOW_RECESS =
  "inset 0 2px 8px rgba(0,0,0,0.78), inset 0 -1px 0 rgba(232,236,239,0.06)";

// Light helpers
export const edgeLight = (color = "#ffbf70", a = 0.5) =>
  `inset 0 1px 0 rgba(${color === "#ffbf70" ? "255,191,112" : "232,236,239"},${a})`;
export const bloom = (color = "#f0984a") =>
  `0 0 14px ${color}66, 0 0 34px ${color}2e, inset 0 0 22px ${color}1f`;
export const textGlow = (color = "#f0984a") =>
  `0 0 10px ${color}88, 0 1px 2px #000`;
export const TEXT_SET = "0 1px 2px rgba(0,0,0,0.9)";

// ── Glow helpers (legacy signatures preserved) ────────────────
export const shimmerBorder = (color = "#d98e3a") =>
  `0 0 0 1px ${color}99, 0 0 12px ${color}55, 0 0 24px ${color}28`;
export const inkGlow = (color = "#c8763a") =>
  `0 0 18px ${color}3c, inset 0 1px 0 ${color}2e`;
